import { motion } from "framer-motion";
import { FOUNDER_PHOTO } from "../../lib/site";
import { Reveal } from "../../lib/motion";

export default function About() {
  return (
    <section
      data-testid="about-section"
      className="bg-white relative overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Photo — treated with clipped spotlight frame */}
          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto">
              {/* offset blocks */}
              <motion.div
                initial={{ opacity: 0, x: -30, y: 30 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -bottom-5 -left-5 w-full h-full rounded-3xl bg-[#FF007F] border-2 border-[#1A1A1A]"
              />
              <motion.div
                initial={{ opacity: 0, x: 30, y: -30 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -top-5 -right-5 w-full h-full rounded-3xl bg-[#FFD700] border-2 border-[#1A1A1A]"
              />

              {/* Clipped spotlight photo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-3xl border-2 border-[#1A1A1A] overflow-hidden nb-shadow-lg bg-[#1A1A1A] aspect-square"
              >
                <img
                  src={FOUNDER_PHOTO}
                  alt="Julija McDowell — founder of TinkerTank and TinkerShift"
                  className="w-full h-full object-cover"
                  data-testid="founder-photo"
                />
                {/* Spotlight vignette */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,transparent_38%,rgba(0,0,0,0.55)_100%)]" />
                {/* Corner clipped frame */}
                <svg
                  className="pointer-events-none absolute inset-0 w-full h-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  {[
                    "M4 4 L18 4 M4 4 L4 18",
                    "M96 4 L82 4 M96 4 L96 18",
                    "M4 96 L18 96 M4 96 L4 82",
                    "M96 96 L82 96 M96 96 L96 82",
                  ].map((d, i) => (
                    <path
                      key={i}
                      d={d}
                      stroke="#FFD700"
                      strokeWidth="1.2"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </svg>
                {/* Scan grain */}
                <div className="pointer-events-none absolute inset-0 bg-dots-light opacity-20 mix-blend-overlay" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20, rotate: 0 }}
                whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -bottom-6 -right-6 bg-white border-2 border-[#1A1A1A] rounded-2xl px-4 py-3 nb-shadow"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#0055FF]">
                  Founder
                </p>
                <p className="font-display font-black text-lg leading-tight">
                  Julija McDowell
                </p>
              </motion.div>
            </div>
          </div>

          {/* Copy */}
          <Reveal className="lg:col-span-7">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#0055FF]">
              About
            </p>
            <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              Built by educators, makers and{" "}
              <span className="bg-[#00C2B8] px-2 inline-block">creative</span>{" "}
              industry thinkers.
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#4A4A4A] font-medium leading-relaxed max-w-2xl">
              <strong className="text-[#1A1A1A]">Julija McDowell</strong>, co-founder
              and educational trailblazer, brings over two decades of expertise
              in training, computer animation and film — notably as one of the
              first female animators at George Lucas’s{" "}
              <strong className="text-[#1A1A1A]">
                Industrial Light + Magic
              </strong>
              .
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] font-medium leading-relaxed max-w-2xl">
              The visionary mind behind{" "}
              <strong className="text-[#1A1A1A]">TinkerTank</strong>,
              Australia’s premier tech-based makerspace, Julija leads the charge
              in STEAM education. Her innovative programs empower students and
              support educators — solidifying her reputation as a driving force
              in the educational revolution, where expertise, creativity and
              passion converge.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {[
                "Co-founder",
                "ILM alum",
                "Animator",
                "Educator",
                "STEAM advocate",
              ].map((t) => (
                <span
                  key={t}
                  className="bg-white border-2 border-[#1A1A1A] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest nb-shadow-sm"
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
